import React, { useState } from "react";

// App
import EntitySelector from "../training/entitySelector";
import { serverUrl } from "../../config";

// UI components
import { Col, Row } from "react-bootstrap";
import {
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import SlideshowIcon from "@mui/icons-material/SlideshowOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import MicIcon from "@mui/icons-material/MicNoneRounded";
import CollectionsIcon from "@mui/icons-material/CollectionsOutlined";
import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import EventIcon from "@mui/icons-material/CalendarTodayOutlined";

// Content types a buyer can consume anonymously — keep in sync with the
// dsr module's SalesRoom::referenceFieldSettings().
export const ROOM_CONTENT_TYPES = [
  "presentation",
  "document",
  "video",
  "link",
  "podcast",
  "gallery",
  "news",
  "event",
];

const typeIcons = {
  presentation: SlideshowIcon,
  document: DescriptionIcon,
  video: MovieIcon,
  link: LinkIcon,
  podcast: MicIcon,
  gallery: CollectionsIcon,
  news: ArticleIcon,
  event: EventIcon,
};

const newSectionId = () =>
  `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const createSection = () => ({
  id: newSectionId(),
  title: "",
  description: "",
  items: [],
});

export default function SectionBuilder(props) {
  const { sections, setSections } = props;

  const [showSelector, setShowSelector] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);

  const updateSection = (sectionId, changes) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, ...changes } : section
      )
    );
  };

  const removeSection = (sectionId) => {
    setSections(sections.filter((section) => section.id !== sectionId));
  };

  const moveSection = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sections.length) {
      return;
    }
    const reordered = [...sections];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setSections(reordered);
  };

  const moveItem = (section, index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= section.items.length) {
      return;
    }
    const items = [...section.items];
    [items[index], items[target]] = [items[target], items[index]];
    updateSection(section.id, { items });
  };

  const removeItem = (section, index) => {
    updateSection(section.id, {
      items: section.items.filter((_, i) => i !== index),
    });
  };

  const activeSection = sections.find(
    (section) => section.id === activeSectionId
  );

  const itemThumbnail = (item) => {
    if (item.thumbnail) {
      const src =
        item.thumbnail.indexOf("http") === 0
          ? item.thumbnail
          : `${serverUrl}${item.thumbnail}`;
      return <img src={src} alt={item.title} />;
    }

    const TypeIcon = typeIcons[item.type] || DescriptionIcon;
    return <TypeIcon className="section-item__type-icon" />;
  };

  return (
    <div className="section-builder">
      {sections.length === 0 && (
        <Typography owner="admin" variant="body2" className="mb-2">
          No sections yet. Group your content into sections like
          “Proposal”, “Case Studies”, or “Pricing”.
        </Typography>
      )}

      {sections.map((section, sectionIndex) => (
        <div className="section-builder__section mb-3" key={section.id}>
          <Row className="align-items-center gx-2 mb-2">
            <Col xs={12} md={5}>
              <TextField
                className="w-100"
                variant="outlined"
                size="small"
                value={section.title}
                sx={{ "& .MuiInputBase-input": { height: 28 } }}
                placeholder="Section title"
                onInput={(e) =>
                  updateSection(section.id, { title: e.target.value })
                }
              />
            </Col>
            <Col xs={12} md={5}>
              <TextField
                className="w-100"
                variant="outlined"
                size="small"
                value={section.description}
                sx={{ "& .MuiInputBase-input": { height: 28 } }}
                placeholder="Section description (optional)"
                onInput={(e) =>
                  updateSection(section.id, { description: e.target.value })
                }
              />
            </Col>
            <Col xs={12} md={2} className="d-flex justify-content-end">
              <IconButton
                size="small"
                disabled={sectionIndex === 0}
                onClick={() => moveSection(sectionIndex, -1)}
                title="Move section up"
              >
                <ArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={sectionIndex === sections.length - 1}
                onClick={() => moveSection(sectionIndex, 1)}
                title="Move section down"
              >
                <ArrowDownIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => removeSection(section.id)}
                title="Remove section"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Col>
          </Row>

          {section.items.length > 0 && (
            <div className="section-builder__items mb-2">
              {section.items.map((item, itemIndex) => (
                <Row
                  className="section-item align-items-center gx-2 py-1"
                  key={`${section.id}-item-${item.type}-${item.id}`}
                >
                  <Col xs="auto" className="section-item__thumbnail">
                    {itemThumbnail(item)}
                  </Col>
                  <Col className="flex-grow-1">
                    <Typography owner="admin" variant="body2">
                      {item.title}
                    </Typography>
                    <Typography
                      owner="admin"
                      variant="subtitle2"
                      className="section-item__type"
                    >
                      {item.type}
                    </Typography>
                  </Col>
                  <Col xs="auto">
                    <IconButton
                      size="small"
                      disabled={itemIndex === 0}
                      onClick={() => moveItem(section, itemIndex, -1)}
                    >
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={itemIndex === section.items.length - 1}
                      onClick={() => moveItem(section, itemIndex, 1)}
                    >
                      <ArrowDownIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => removeItem(section, itemIndex)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Col>
                </Row>
              ))}
            </div>
          )}

          <Button
            variant="outlined"
            size="small"
            sx={{ textTransform: "none" }}
            startIcon={<AddIcon />}
            onClick={() => {
              setActiveSectionId(section.id);
              setShowSelector(true);
            }}
          >
            Add content
          </Button>
        </div>
      ))}

      <Button
        variant="contained"
        color="secondary"
        className="action"
        size="small"
        sx={{ textTransform: "none" }}
        startIcon={<AddIcon />}
        onClick={() => setSections([...sections, createSection()])}
      >
        Add section
      </Button>

      {showSelector && (
        <EntitySelector
          showSelector={showSelector}
          setShowSelector={setShowSelector}
          allowedTypes={ROOM_CONTENT_TYPES}
          currentBlock={{ data: { entities: activeSection?.items || [] } }}
          insertSelections={(selection) => {
            if (activeSectionId) {
              updateSection(activeSectionId, { items: selection });
            }
          }}
        />
      )}
    </div>
  );
}
